import { Mechanics } from "game-common/mechanics/mechanics"
import { Coordinate, Entity, EntityId, TileSize } from "game-common/models"
import { Container, Graphics } from "pixi.js"
import { ClientGameLogic } from "../../../client_game_logic"
import { IWorldOverlaySurface } from "../../../client_models"
import { ClientRenderable } from "./entity_rendering_manager"
import { HoldTimer } from "./gui/hold_timer"
import { PickupTally } from "./gui/pickup_tally"
import { RescueHint } from "./gui/rescue_hint"
import { BaseRenderable } from "./sprites/base_renderable"
import { ScalingParams } from "../../../client_util"
import { BombTrajectoryTracer } from "./effects/bomb_trajectory_tracer"
import { FireHalo } from "./effects/fire_halo"
import { AttackWindupWarning } from "./effects/attack_windup_warning"
import { BonecallerWarning } from "./effects/bonecaller_warning"
import { BONECALLER_ALTAR_SOUTH, BonecallerShrine, BonecallerEmergence } from "./effects/bonecaller_shrine"
import { DroppedItemGlint, GlintSubject } from "./effects/dropped_item_glint"
import { ACTIVE_FLOOR_FEATURES, FloorRenderFeatures } from "./floor_render_features"
import { bodyLiftAt, hillLiftOf } from "./hill_lift"
import { hillConfig } from "./hill_layer"
import { BODY_GROUND_OFFSET } from "game-common/lpc/body_placement"
import type { TileMapMetaProvider } from "game-common/tile_map_meta_provider"

export class WorldOverlayItem extends BaseRenderable {
    heldAttackGripWorld?: () => { x: number; y: number; scale: number; hands?: { x: number; y: number }[] } | undefined
    headAttachmentWorld?: () => { x: number; y: number } | undefined
}

const SCALING_CONSTANT = ScalingParams.surfaceZoom
export class WorldOverlaySurface extends Graphics implements IWorldOverlaySurface {
    private entityIdToOverlayItem: Map<EntityId, ClientRenderable> = new Map()
    // Countdown dials for held interactions, one per entity somebody is currently holding (see
    // HoldTimer). Kept here rather than inside AvatarHud because a hold target need not be a
    // character: the hud belongs to the LPC sprites, while EVERY entity has an overlay item, which
    // is already parked above its head and already follows it.
    private holdTimers: Map<EntityId, HoldTimer> = new Map()
    // The "hold on her to help her up" prompt over an injured rescue survivor, kept here for the
    // same reason as the dial above: it hangs off the entity's overlay item, so it follows her
    // without any per-frame arithmetic. At most one of these exists in a room (there is one
    // survivor per camp), and only while the server is asking for it.
    private rescueHints: Map<EntityId, RescueHint> = new Map()
    // The running "+3" beside the player while they gather (see PickupTally), kept here for the
    // dial's reason: it hangs off the overlay item, which already follows the body.
    private pickupTallies: Map<EntityId, PickupTally> = new Map()
    private fireHalos = new Map<EntityId, FireHalo>()
    private fadingTracers = new Set<BombTrajectoryTracer>()
    private attackWarnings = new Map<EntityId, AttackWindupWarning>()
    private bonecallerWarnings: Map<EntityId, BonecallerWarning> = new Map()
    private bonecallerShrines: Map<EntityId, BonecallerShrine> = new Map()
    private bonecallerEmergences: Map<EntityId, BonecallerEmergence> = new Map()
    // Dropped loot, and the glints currently drawn for it. Two maps rather than one: the
    // subject is registered once when the item's renderable is built, while the glint itself
    // comes and goes with the server's noGlint flag.
    private droppedItems: Map<EntityId, GlintSubject> = new Map()
    private droppedItemGlints: Map<EntityId, DroppedItemGlint> = new Map()

    logic: ClientGameLogic

    constructor(logic: ClientGameLogic, private groundSurface?: Container,
        private features: Readonly<FloorRenderFeatures> = ACTIVE_FLOOR_FEATURES) {
        super()
        this.logic = logic
        if (features.interactions) this.exposeHoldTimerTrigger()
        // Overlay items paint by zIndex so a speaking entity can lift its HUD (chat bubble) above the
        // nameplates/role glyphs of the entities around it -- see AvatarHud.liftOverlay. Items all rest
        // at zIndex 0, where pixi's stable sort keeps the old insertion order.
        this.sortableChildren = true
    }

    offer = (entity: Entity): WorldOverlayItem => {
        const renderable = new WorldOverlayItem()
        renderable.zIndex = 0
        // A renderable can be rebuilt under a live entity (an appearance change), which lands here
        // again with a fresh item. Any dial is still inside the OLD one, where nothing will ever
        // move it again; drop it and the next message raises it on the new item.
        this.dropHoldTimer(entity.entityId)
        this.dropRescueHint(entity.entityId)
        this.dropPickupTally(entity.entityId)
        this.dropFireHalo(entity.entityId)
        this.entityIdToOverlayItem.set(entity.entityId, renderable)

        const f = 32 * SCALING_CONSTANT
        renderable.beginFill(0xff0000, 0.01)
        renderable.drawRect(0, 0, f, f)
        renderable.endFill()

        renderable.width = f
        renderable.height = f
        this.addChild(renderable)

        return renderable
    }

    remove = (entity: Entity) => {
        if (!entity) {
            return
        }
        this.dropFireHalo(entity.entityId)
        this.dropAttackWarning(entity.entityId)
        this.dropBonecallerWarning(entity.entityId)
        this.dropDroppedItemGlint(entity.entityId)
        this.droppedItems.delete(entity.entityId)
        this.dropRitualArt(entity.entityId)
        const renderable = this.entityIdToOverlayItem.get(entity.entityId)
        if (!renderable) {
            return
        }

        renderable.remove()
        this.entityIdToOverlayItem.delete(entity.entityId)
        this.dropHoldTimer(entity.entityId)
        this.dropRescueHint(entity.entityId)
        this.dropPickupTally(entity.entityId)
    }

    // An entity leaving takes its overlay item with it, but only DETACHES it (BaseRenderable.remove),
    // so a dial left inside would keep its Graphics and its Text for the rest of the session.
    /**
     * `holdTimer("lord")` in the console raises a countdown dial over the first entity whose name
     * matches, for as long as given, so the thing can be looked at and tuned.
     *
     * The real trigger is a held interaction on a rescue survivor, which takes a wave-3 clearance and
     * a fight at the wreck to reach -- there would otherwise be no way to check the dial's placement,
     * its colours or its endings against real art. It draws only: the server knows nothing about it,
     * and the next message from that entity takes it straight back down.
     */
    private exposeHoldTimerTrigger = () => {
        if (typeof window === "undefined") {
            return
        }
        ;(window as any).holdTimer = (name?: string, durationMs: number = 2000): string => {
            const match = this.logic?.entityManager
                ?.collect((e: Entity) => !name || (e.name || "").toLowerCase().includes(name.toLowerCase()))
                .find((e: Entity) => this.entityIdToOverlayItem.has(e.entityId))
            if (!match) {
                return `[holdTimer] no on-screen entity matching ${name ?? "(any)"}`
            }
            this.updateHoldTimer({ ...match, holdRemainingMs: durationMs, holdDurationMs: durationMs } as Entity)
            return `[holdTimer] ${match.name || match.entityId} for ${durationMs}ms`
        }
    }

    private dropHoldTimer = (entityId: EntityId) => {
        const timer = this.holdTimers.get(entityId)
        if (!timer) {
            return
        }
        this.holdTimers.delete(entityId)
        timer.parent?.removeChild(timer)
        if (!timer.destroyed) {
            timer.destroy({ children: true })
        }
    }

    /** Count `quantity` more of `key` beside this entity -- a pickup that has just landed. */
    countPickup = (entityId: EntityId, key: string, quantity: number, icon: () => Container | undefined) => {
        let tally = this.pickupTallies.get(entityId)
        if (!tally) {
            const item = this.entityIdToOverlayItem.get(entityId)
            if (!item) {
                return
            }
            tally = new PickupTally()
            item.addChild(tally)
            this.pickupTallies.set(entityId, tally)
        }
        tally.add(key, quantity, icon)
    }

    /** Sweep the counts once a frame; empty except while somebody is gathering. */
    animatePickupTallies = () => {
        if (this.pickupTallies.size === 0) {
            return
        }
        const now = performance.now()
        this.pickupTallies.forEach((tally, entityId) => {
            if (tally.destroyed || !this.entityIdToOverlayItem.has(entityId) || !tally.animate(now)) {
                this.dropPickupTally(entityId)
            }
        })
    }

    private dropPickupTally = (entityId: EntityId) => {
        const tally = this.pickupTallies.get(entityId)
        if (!tally) {
            return
        }
        this.pickupTallies.delete(entityId)
        tally.parent?.removeChild(tally)
        if (!tally.destroyed) {
            tally.destroy({ children: true })
        }
    }

    private dropRescueHint = (entityId: EntityId) => {
        const hint = this.rescueHints.get(entityId)
        if (!hint) {
            return
        }
        this.rescueHints.delete(entityId)
        hint.parent?.removeChild(hint)
        if (!hint.destroyed) {
            hint.destroy({ children: true })
        }
    }

    updateEntityId = (originalEntityId: EntityId, updatedEntityId: EntityId) => {
        this.entityIdToOverlayItem.set(updatedEntityId, this.entityIdToOverlayItem.get(originalEntityId))
        this.entityIdToOverlayItem.delete(originalEntityId)
        const halo = this.fireHalos.get(originalEntityId)
        if (halo) {
            this.fireHalos.set(updatedEntityId, halo)
            this.fireHalos.delete(originalEntityId)
        }
        const glint = this.droppedItemGlints.get(originalEntityId)
        if (glint) {
            this.droppedItemGlints.set(updatedEntityId, glint)
            this.droppedItemGlints.delete(originalEntityId)
        }
        const dropped = this.droppedItems.get(originalEntityId)
        if (dropped) {
            this.droppedItems.set(updatedEntityId, dropped)
            this.droppedItems.delete(originalEntityId)
        }
        const warning = this.bonecallerWarnings.get(originalEntityId)
        if (warning) {
            this.bonecallerWarnings.set(updatedEntityId, warning)
            this.bonecallerWarnings.delete(originalEntityId)
        }
        for (const map of [this.bonecallerShrines, this.bonecallerEmergences]) {
            const art = map.get(originalEntityId)
            if (art) { (map as Map<EntityId, Graphics>).set(updatedEntityId, art); map.delete(originalEntityId) }
        }
        // The dial travels with the item it is parented to, so only its key moves.
        const timer = this.holdTimers.get(originalEntityId)
        if (timer) {
            this.holdTimers.set(updatedEntityId, timer)
            this.holdTimers.delete(originalEntityId)
        }
        const hint = this.rescueHints.get(originalEntityId)
        if (hint) {
            this.rescueHints.set(updatedEntityId, hint)
            this.rescueHints.delete(originalEntityId)
        }
    }

    /** `provider`: the map of the room the entity is drawn in, whose hill it stands on (hill_lift.ts). */
    updateEntity = (entity: Entity, provider: TileMapMetaProvider | undefined = this.logic?.tilemapMetaProvider) => {
        if (!entity) {
            return
        }
        // Before the early return below: a dropped item has a glint but no overlay item of its own.
        this.updateDroppedItemGlint(entity)

        const renderable = this.entityIdToOverlayItem.get(entity.entityId)
        if (!renderable) {
            return
        }

        const yScaler = Mechanics.entity.body.isShort(entity) ? 0.75 : 1.25
        renderable.visible = entity.visible
        renderable.x = entity.location.x * SCALING_CONSTANT - TileSize * 0.25 * SCALING_CONSTANT
        // Over the body as it is drawn: raised with it on a hill.
        const y = entity.location.y - hillLiftOf(entity, provider)
        renderable.y = y * SCALING_CONSTANT - TileSize * yScaler * SCALING_CONSTANT
        this.updateFireHalo(entity)
        this.updateAttackWarning(entity)
        this.updateBonecallerWarning(entity, provider)
        this.updateRitualArt(entity, provider)
    }

    /**
     * Register a dropped item so it can glint. Called once, when its renderable is built; the
     * glint reads the renderable's live transform and texture rather than the entity's location,
     * so a spinning collectable's light turns with it.
     */
    offerDroppedItemGlint = (entityId: EntityId, subject: GlintSubject) => {
        this.droppedItems.set(entityId, subject)
    }

    private updateDroppedItemGlint = (entity: Entity) => {
        const subject = this.droppedItems.get(entity.entityId)
        if (!subject) {
            return
        }
        // The catalog says some drops are too common to be worth announcing (wood, straw, seed),
        // and the flag rides on ordinary entity messages, so it can flip either way under us.
        if (entity.noGlint === true) {
            this.dropDroppedItemGlint(entity.entityId)
            return
        }
        if (this.droppedItemGlints.has(entity.entityId)) {
            return
        }
        const glint = new DroppedItemGlint(entity.entityId, subject)
        this.addChild(glint)
        this.droppedItemGlints.set(entity.entityId, glint)
    }

    private dropDroppedItemGlint = (entityId: EntityId) => {
        const glint = this.droppedItemGlints.get(entityId)
        if (!glint) {
            return
        }
        this.droppedItemGlints.delete(entityId)
        glint.parent?.removeChild(glint)
        glint.destroy({ children: true })
    }

    animateDroppedItemGlints = (now: number = Date.now()) => {
        this.droppedItemGlints.forEach((glint, entityId) => {
            if (!glint.alive()) {
                this.dropDroppedItemGlint(entityId)
                this.droppedItems.delete(entityId)
                return
            }
            glint.animate(now)
        })
    }

    private updateRitualArt = (entity: Entity, provider: TileMapMetaProvider | undefined) => {
        const lift = hillLiftOf(entity, provider)
        if (entity.bonecallerRitual) {
            let shrine = this.bonecallerShrines.get(entity.entityId)
            if (!shrine) {
                // Ground tiles sort below 50 and characters above 100 + feet-y. The HUD overlay
                // sits above the whole world, so a negative overlay zIndex still covers bodies.
                shrine = new BonecallerShrine(this.groundSurface ? 1 : SCALING_CONSTANT)
                shrine.zIndex = this.groundSurface ? 50 : -2
                ;(this.groundSurface || this).addChild(shrine)
                this.bonecallerShrines.set(entity.entityId, shrine)
            }
            shrine.update(entity, Date.now(), lift)
            // Up a hill the altar lies on the hill's surface, which sorts among bodies by its ground rows
            // (hill_layer.ts): over the ground it covers, down to its south rim, and under whoever stands on it.
            if (this.groundSurface) {
                const south = entity.location.y + 16 + BONECALLER_ALTAR_SOUTH
                shrine.zIndex = lift ? 100 + south - BODY_GROUND_OFFSET - hillConfig().shadowAllowance : 50
            }
        }
        if (entity.bonecallerEmergenceRemainingMs && !entity.preSpawnEffectView) {
            let emergence = this.bonecallerEmergences.get(entity.entityId)
            if (!emergence) {
                emergence = new BonecallerEmergence(); emergence.zIndex = -1
                this.addChild(emergence); this.bonecallerEmergences.set(entity.entityId, emergence)
            }
            emergence.update(entity, Date.now(), lift)
        } else {
            const emergence = this.bonecallerEmergences.get(entity.entityId)
            if (emergence) { this.bonecallerEmergences.delete(entity.entityId); emergence.destroy() }
        }
    }

    private dropRitualArt = (id: EntityId) => {
        for (const map of [this.bonecallerShrines, this.bonecallerEmergences]) {
            const art = map.get(id)
            if (!art) continue
            map.delete(id); art.parent?.removeChild(art); art.destroy()
        }
    }

    updateBonecallerWarning = (entity: Entity, provider: TileMapMetaProvider | undefined = this.logic?.tilemapMetaProvider) => {
        if (!entity.bonecallerCombatRadius) {
            this.dropBonecallerWarning(entity.entityId)
            return
        }
        let warning = this.bonecallerWarnings.get(entity.entityId)
        if (!warning) {
            warning = new BonecallerWarning()
            warning.zIndex = -1
            this.addChild(warning)
            this.bonecallerWarnings.set(entity.entityId, warning)
        }
        warning.update(entity, Date.now(), (x, y) => bodyLiftAt(provider, x, y))
    }

    private dropBonecallerWarning = (id: EntityId) => {
        const warning = this.bonecallerWarnings.get(id)
        if (!warning) return
        this.bonecallerWarnings.delete(id)
        warning.parent?.removeChild(warning)
        warning.destroy({ children: true })
    }

    private updateFireHalo = (entity: Entity) => {
        if (entity.hp <= 0 || entity.isDead() || !entity.visualEffects?.includes("firehalo")) {
            this.dropFireHalo(entity.entityId)
            return
        }
        if (this.fireHalos.has(entity.entityId)) return
        const item = this.entityIdToOverlayItem.get(entity.entityId)
        if (!item) return
        const halo = new FireHalo()
        halo.position.set(TileSize * 0.25 * SCALING_CONSTANT, 22 * SCALING_CONSTANT)
        halo.scale.set(SCALING_CONSTANT)
        item.addChild(halo)
        this.fireHalos.set(entity.entityId, halo)
        halo.animate()
    }

    private dropFireHalo = (id: EntityId) => {
        const halo = this.fireHalos.get(id)
        if (!halo) return
        this.fireHalos.delete(id)
        halo.parent?.removeChild(halo)
        halo.destroy()
    }

    private updateAttackWarning = (entity: Entity) => {
        if (!entity.attackWindup || entity.hp <= 0 || entity.isDead()) { this.dropAttackWarning(entity.entityId); return }
        let warning = this.attackWarnings.get(entity.entityId)
        if (!warning) {
            warning = new AttackWindupWarning()
            warning.zIndex = -1
            this.addChild(warning)
            warning.tracer.zIndex = -1
            this.addChild(warning.tracer)
            warning.heldBomb.zIndex = 1
            this.addChild(warning.heldBomb)
            this.attackWarnings.set(entity.entityId, warning)
        }
        warning.update(entity)
    }

    private dropAttackWarning = (id: EntityId) => {
        const warning = this.attackWarnings.get(id)
        if (!warning) return
        this.attackWarnings.delete(id)
        warning.tracer.fade()
        if (warning.tracer.finished()) {
            warning.tracer.parent?.removeChild(warning.tracer)
            warning.tracer.destroy()
        } else this.fadingTracers.add(warning.tracer)
        warning.parent?.removeChild(warning)
        warning.heldBomb.parent?.removeChild(warning.heldBomb)
        warning.heldBomb.destroy()
        warning.destroy()
    }

    animateBonecallerWarnings = () => {
        this.fadingTracers.forEach(tracer => {
            if (tracer.destroyed || tracer.finished()) {
                this.fadingTracers.delete(tracer)
                tracer.parent?.removeChild(tracer)
                if (!tracer.destroyed) tracer.destroy()
            } else tracer.animate()
        })
        this.fireHalos.forEach((halo, id) => {
            const item = this.entityIdToOverlayItem.get(id)
            if (!item || halo.destroyed) { this.dropFireHalo(id); return }
            const head = (item as WorldOverlayItem).headAttachmentWorld?.()
            if (head) halo.position.copyFrom(item.toLocal(head))
            if (item.visible) halo.animate()
        })
        this.attackWarnings.forEach((warning, id) => {
            const item = this.entityIdToOverlayItem.get(id)
            if (!item || warning.destroyed) { this.dropAttackWarning(id); return }
            warning.animate(Date.now(), item.visible, (item as WorldOverlayItem).heldAttackGripWorld?.())
        })
        for (const map of [this.bonecallerShrines, this.bonecallerEmergences]) {
            map.forEach((art, id) => {
                const item = this.entityIdToOverlayItem.get(id)
                if (!item || art.destroyed) { this.dropRitualArt(id); return }
                art.visible = item.visible
                if (art.visible) art.animate()
            })
        }
        this.bonecallerWarnings.forEach((warning, id) => {
            const item = this.entityIdToOverlayItem.get(id)
            if (!item || warning.destroyed) { this.dropBonecallerWarning(id); return }
            const player = this.logic?.playerEntity
            warning.setBoundaryVisible(item.visible && (!player || warning.viewerInside(player.location)))
            warning.animate(Date.now(), item.visible)
        })
    }

    getEntityRenderable = (entityId: EntityId): ClientRenderable => {
        return this.entityIdToOverlayItem.get(entityId)
    }

    /**
     * The server's verdict on whether a held interaction is running against this entity, straight
     * off the wire (see Message.holdRemainingMs). A remaining time raises the dial and keeps it
     * anchored; no remaining time ends whichever one is up.
     *
     * Called for every entity message, so it has to be cheap and idempotent in the common case of
     * no hold at all: that is a single failed Map lookup.
     */
    updateHoldTimer = (entity: Entity) => {
        if (!this.features.interactions) return
        const existing = this.holdTimers.get(entity.entityId)
        if (!(entity.holdRemainingMs > 0)) {
            // The dial is not dropped here -- `stop` starts its ending, and animateHoldTimers takes
            // it down once that has played. Stopping twice is a no-op, which matters because this
            // arrives on every message for as long as the entity is in range.
            existing?.stop()
            return
        }
        const timer = existing || this.attachHoldTimer(entity.entityId)
        timer?.setRemaining(entity.holdRemainingMs, entity.holdDurationMs)
    }

    /**
     * The server's verdict on whether the "hold on her to help her up" prompt belongs over this
     * entity, straight off the wire (see Message.rescueHelpHint). Raising it is the rescue's
     * decision, never this surface's: the hold is refused while a skeleton still stands over the
     * wreck, so the prompt waits for the fight to end.
     *
     * Called for every entity message, so -- like updateHoldTimer above -- it has to be cheap and
     * idempotent in the common case of no prompt at all: one failed Map lookup.
     */
    updateRescueHint = (entity: Entity) => {
        if (!this.features.interactions) return
        const existing = this.rescueHints.get(entity.entityId)
        if (!entity.rescueHelpHint) {
            // Not dropped here: `stop` starts its ending and animateRescueHints takes it down once
            // that has played. Stopping twice is a no-op, which matters because this arrives on
            // every message for as long as the entity is in range.
            existing?.stop()
            return
        }
        const hint = existing || this.attachRescueHint(entity.entityId)
        hint?.show()
    }

    private attachRescueHint = (entityId: EntityId): RescueHint | undefined => {
        const item = this.entityIdToOverlayItem.get(entityId)
        if (!item) {
            return undefined
        }
        const hint = new RescueHint()
        // Added after the hud, so it draws over the name plate rather than under it.
        item.addChild(hint)
        this.rescueHints.set(entityId, hint)
        return hint
    }

    /**
     * Sweep the live prompt, once a frame. There is at most one in a room and only during the beat
     * between the wreck being cleared and the survivor being helped up, so this is empty almost
     * always -- the early return is what keeps it off the frame budget the rest of the time.
     */
    animateRescueHints = () => {
        if (this.rescueHints.size === 0) {
            return
        }
        const now = Date.now()
        this.rescueHints.forEach((hint, entityId) => {
            // An entity that left took its overlay item -- and this prompt with it -- so there is
            // nothing left to animate and the entry would otherwise be held for the session.
            if (hint.destroyed || !this.entityIdToOverlayItem.has(entityId)) {
                this.dropRescueHint(entityId)
                return
            }
            if (!hint.animate(now)) {
                this.dropRescueHint(entityId)
            }
        })
    }

    private attachHoldTimer = (entityId: EntityId): HoldTimer | undefined => {
        const item = this.entityIdToOverlayItem.get(entityId)
        if (!item) {
            return undefined
        }
        const timer = new HoldTimer()
        // Added after the hud, so it draws over the name plate it sits above.
        item.addChild(timer)
        this.holdTimers.set(entityId, timer)
        return timer
    }

    /**
     * Sweep every live dial, once a frame. A hold lasts a couple of seconds and only one entity at
     * a time is usually being held, so this is empty almost always and tiny when it is not.
     */
    animateHoldTimers = () => {
        if (this.holdTimers.size === 0) {
            return
        }
        const now = Date.now()
        this.holdTimers.forEach((timer, entityId) => {
            // An entity that left the room took its overlay item -- and this dial with it -- down
            // with it, so there is nothing left to animate and the entry would otherwise be held
            // for the session.
            if (timer.destroyed || !this.entityIdToOverlayItem.has(entityId)) {
                this.dropHoldTimer(entityId)
                return
            }
            if (!timer.animate(now)) {
                this.dropHoldTimer(entityId)
            }
        })
    }

    computeLocation = (x: number, y: number): Coordinate => {
        return {
            x: x * SCALING_CONSTANT,
            y: y * SCALING_CONSTANT,
        }
    }

    // Convert an overlay-local pointer into the frame that actually draws the world.
    // HUD coordinates use surfaceZoom, but its pivot is independent of the ground's:
    // dividing by surfaceZoom alone is 16 world pixels off with the house's 32px pivot.
    worldLocation = (x: number, y: number): Coordinate => {
        if (this.groundSurface) {
            const point = this.groundSurface.toLocal(this.toGlobal({ x, y }))
            return { x: point.x, y: point.y }
        }
        return {
            x: x / SCALING_CONSTANT,
            y: y / SCALING_CONSTANT,
        }
    }
}
